import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ClipboardList, Save, Copy, ArrowLeft, GripVertical, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "select", label: "Dropdown" },
  { value: "checkbox", label: "Yes / No" },
  { value: "date", label: "Date" },
  { value: "number", label: "Number" },
];

function slugify(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40) || `f_${Math.random().toString(36).slice(2, 6)}`;
}

export default function IncidentTemplates() {
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const load = () => api.get("/incident-templates").then(({ data }) => setTemplates(data));
  useEffect(() => { load(); }, []);

  const createNew = async () => {
    try {
      const { data } = await api.post("/incident-templates", {
        name: "New report type",
        description: "",
        fields: [],
        recipients: [],
        enabled: true,
        cc_reporter: true,
        color: "#ff3b5c",
      });
      await load();
      setActiveId(data.id);
      toast.success("Template created");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template? Existing reports keep their data.")) return;
    await api.delete(`/incident-templates/${id}`);
    if (activeId === id) setActiveId(null);
    load();
  };

  const active = templates.find((t) => t.id === activeId);

  return (
    <div className="space-y-6" data-testid="incident-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/incidents" className="text-xs text-slate-400 hover:text-[color:var(--accent)] inline-flex items-center gap-1 mb-2">
            <ArrowLeft size={12} /> Back to incidents
          </Link>
          <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Admin</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Incident Report Templates</h1>
          <p className="text-sm text-slate-400 mt-1">Build any report type your team needs. Set who gets emailed. Emails use your company branding.</p>
        </div>
        <button onClick={createNew} data-testid="new-template" className="px-4 py-2.5 rounded-lg font-bold text-black inline-flex items-center gap-2" style={{ background: "var(--accent)" }}>
          <Plus size={16} /> New template
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="card-surface p-3 md:sticky md:top-4 md:self-start">
          {templates.length === 0 && <div className="text-sm text-slate-500 p-3">No templates yet. Click "New template" to build your first one.</div>}
          <ul className="space-y-1">
            {templates.map((t) => (
              <li key={t.id}>
                <button
                  onClick={() => setActiveId(t.id)}
                  data-testid={`tpl-select-${t.id}`}
                  className={`w-full text-left rounded-lg px-3 py-2 flex items-center gap-2 hover:bg-[#0b0d12] ${activeId === t.id ? "bg-[#0b0d12] border border-[color:var(--accent)]/40" : "border border-transparent"}`}
                >
                  <ClipboardList size={14} style={{ color: t.color || "#ff3b5c" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{t.name}</div>
                    <div className="text-[11px] text-slate-500">{(t.fields || []).length} fields · {(t.recipients || []).length} recipients</div>
                  </div>
                  {!t.enabled && <span className="text-[10px] text-slate-500 uppercase">off</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          {active ? <TemplateEditor key={active.id} template={active} onSaved={load} onDelete={() => remove(active.id)} /> : (
            <div className="card-surface p-10 text-center text-slate-500 text-sm">Select a template on the left to edit it, or click <strong className="text-slate-300">New template</strong>.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplateEditor({ template, onSaved, onDelete }) {
  const [f, setF] = useState(template);
  const [busy, setBusy] = useState(false);
  const [recipientsText, setRecipientsText] = useState((template.recipients || []).join(", "));

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const addField = () => {
    const id = `field_${Math.random().toString(36).slice(2, 6)}`;
    set("fields", [...(f.fields || []), { id, label: "New field", type: "text", required: false, options: [] }]);
  };
  const removeField = (idx) => {
    const next = [...(f.fields || [])]; next.splice(idx, 1); set("fields", next);
  };
  const updateField = (idx, patch) => {
    const next = [...(f.fields || [])];
    const merged = { ...next[idx], ...patch };
    if (patch.label && next[idx].label === next[idx].id.replace(/_/g, " ")) {
      // Auto slug if id was default; otherwise keep it stable
    }
    if (patch.label !== undefined && !next[idx].id) merged.id = slugify(patch.label);
    next[idx] = merged;
    set("fields", next);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...f,
        recipients: recipientsText.split(/[,\n;\s]+/).map((s) => s.trim()).filter((s) => s.includes("@")),
        fields: (f.fields || []).map((fld) => ({ ...fld, id: fld.id || slugify(fld.label) })),
      };
      await api.patch(`/incident-templates/${template.id}`, payload);
      await onSaved();
      toast.success("Template saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)] text-sm w-full";

  return (
    <div className="card-surface p-6 space-y-5" data-testid="template-editor">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="tpl-name" placeholder="Template name" className="w-full bg-transparent border-none outline-none text-2xl font-black" />
          <input value={f.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="tpl-desc" placeholder="Short description shown when picking this report type" className="w-full bg-transparent border-none outline-none text-sm text-slate-400 mt-1" />
        </div>
        <div className="flex flex-col gap-2 items-end">
          <label className="inline-flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" checked={f.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="tpl-enabled" className="accent-[color:var(--accent)] w-4 h-4" />
            Enabled
          </label>
          <input type="color" value={f.color || "#ff3b5c"} onChange={(e) => set("color", e.target.value)} data-testid="tpl-color" className="w-10 h-8 bg-transparent border border-slate-700 rounded cursor-pointer" title="Accent color for this template's emails" />
        </div>
      </div>

      {/* RECIPIENTS */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1.5"><Mail size={12} /> Email recipients</div>
        <textarea rows={2} value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} data-testid="tpl-recipients" placeholder="mods@company.com, trust@company.com" className={inputCls + " resize-none"} />
        <label className="inline-flex items-center gap-2 mt-2 text-xs text-slate-400">
          <input type="checkbox" checked={f.cc_reporter} onChange={(e) => set("cc_reporter", e.target.checked)} data-testid="tpl-cc-reporter" className="accent-[color:var(--accent)] w-4 h-4" />
          Also send a copy to the reporter (recommended)
        </label>
      </div>

      {/* FIELDS */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-slate-400">Fields on the form</div>
          <button type="button" onClick={addField} data-testid="tpl-add-field" className="text-xs px-2 py-1 rounded-lg border border-slate-700 hover:border-[color:var(--accent)] text-slate-300 inline-flex items-center gap-1">
            <Plus size={12} /> Add field
          </button>
        </div>
        <div className="space-y-2">
          {(f.fields || []).length === 0 && <div className="text-xs text-slate-500 py-3">No custom fields yet. This template will still capture title, summary, and severity.</div>}
          {(f.fields || []).map((fld, idx) => (
            <div key={fld.id || `field-${idx}`} className="border border-slate-800 rounded-lg p-3 space-y-2" data-testid={`tpl-field-${idx}`}>
              <div className="flex items-center gap-2">
                <GripVertical size={14} className="text-slate-600" />
                <input value={fld.label} onChange={(e) => updateField(idx, { label: e.target.value })} placeholder="Field label" className={inputCls + " flex-1"} data-testid={`tpl-field-label-${idx}`} />
                <select value={fld.type} onChange={(e) => updateField(idx, { type: e.target.value })} className={inputCls + " !w-40"} data-testid={`tpl-field-type-${idx}`}>
                  {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <label className="inline-flex items-center gap-1 text-xs text-slate-400 whitespace-nowrap">
                  <input type="checkbox" checked={fld.required} onChange={(e) => updateField(idx, { required: e.target.checked })} className="accent-[color:var(--accent)] w-4 h-4" data-testid={`tpl-field-req-${idx}`} /> Required
                </label>
                <button type="button" onClick={() => removeField(idx)} className="text-slate-500 hover:text-[#ff3b5c]" data-testid={`tpl-field-del-${idx}`}><Trash2 size={14} /></button>
              </div>
              {fld.type === "select" && (
                <input value={(fld.options || []).join(", ")} onChange={(e) => updateField(idx, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Options, comma-separated" className={inputCls} data-testid={`tpl-field-opts-${idx}`} />
              )}
              <input value={fld.help || ""} onChange={(e) => updateField(idx, { help: e.target.value })} placeholder="Help text shown under the field (optional)" className={inputCls} data-testid={`tpl-field-help-${idx}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between pt-3 border-t border-slate-800">
        <button type="button" onClick={onDelete} data-testid="tpl-delete" className="text-xs text-slate-400 hover:text-[#ff3b5c] inline-flex items-center gap-1"><Trash2 size={12} /> Delete template</button>
        <button type="button" disabled={busy} onClick={save} data-testid="tpl-save" className="px-4 py-2 rounded-lg font-bold text-black text-sm inline-flex items-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)" }}>
          <Save size={14} /> {busy ? "Saving…" : "Save template"}
        </button>
      </div>
    </div>
  );
}
